import React, { useMemo } from "react";
import { formatSeconds } from "lib/utils";
import { ATTENDANCE_TYPE, getHolidays } from "lib/attendance";
import moment from "moment";
import EditIcon from "@mui/icons-material/Edit";
import DeleteIcon from "@mui/icons-material/Delete";
import { IconButton } from "@mui/material";

const AttendanceTable = ({
  employees,
  data,
  timeRange,
  onEditClick,
  onDeleteClick,
}) => {
  const daysInMonth = timeRange.from.daysInMonth();

  const timeData = useMemo(() => {
    return employees.map((employee) => {
      const row = { employee };
      let theoreticalHours = 0;
      let totalAbsentHours = 0;
      const year = timeRange.from.year();
      const holidays = getHolidays(year);

      for (let i = 0; i < daysInMonth; i++) {
        const dayMoment = timeRange.from.clone().add(i, "day");
        const dayStr = dayMoment.format("YYYY-MM-DD");
        const isWeekend = dayMoment.isoWeekday() >= 6;
        const isHoliday = holidays.includes(dayStr);

        if (isWeekend || isHoliday) {
          row[`day_${i}`] = {
            display: isHoliday ? "F" : "R",
            color: "#ff0000",
            isWeekend: true,
            textColor: "#fff",
          };
        } else {
          theoreticalHours += 8;

          const record = data.find((itm) => {
            const recordDate = moment(itm.date).format("YYYY-MM-DD");
            return itm.employee_id === employee.id && recordDate === dayStr;
          });

          if (record) {
            const typeConfig = ATTENDANCE_TYPE.find(
              (t) => t.type === record.type,
            );
            const isPermission = record.type === "P" || record.type === "S";
            row[`day_${i}`] = {
              display: isPermission ? record.hour : record.type,
              color: typeConfig ? typeConfig.color : "transparent",
              isType: true,
              record: record,
            };
            totalAbsentHours += parseFloat(record.hour || 0);
          } else {
            row[`day_${i}`] = {
              display: "8",
              color: "transparent",
              isDefault: true,
            };
          }
        }
      }

      row["total"] = (theoreticalHours - totalAbsentHours) * 3600;
      return row;
    });
  }, [employees, data, timeRange, daysInMonth]);

  const RenderHeaderDays = () => {
    return Array.from({ length: daysInMonth }).map((_, i) => {
      const dayMoment = timeRange.from.clone().add(i, "day");
      const isWeekend = dayMoment.isoWeekday() > 5;
      return (
        <th
          key={i}
          className={isWeekend ? "weekend" : ""}
          style={{ textAlign: "center", minWidth: "35px" }}
        >
          {dayMoment.format("DD")}
          <br />
          <small style={{ fontWeight: "normal" }}>
            {dayMoment.format("dd")}
          </small>
        </th>
      );
    });
  };

  // Sotto-componente per le celle dei giorni
  const RenderRowCells = ({ rowData }) => {
    const cells = Array.from({ length: daysInMonth }).map((_, i) => {
      const cell = rowData[`day_${i}`];
      const shouldBeWhite = cell.isType || cell.isWeekend;
      const currentDay = timeRange.from
        .clone()
        .add(i, "day")
        .format("YYYY-MM-DD");
      return (
        <td
          key={i}
          onClick={() =>
            !cell.isWeekend &&
            onEditClick(rowData.employee, currentDay, cell.record)
          }
          style={{
            backgroundColor: cell.color,
            textAlign: "center",
            padding: 0,
            border: "1px solid #eee",
            cursor: cell.isWeekend ? "default" : "pointer",
          }}
        >
          <span
            style={{
              color: shouldBeWhite ? "#fff" : "inherit",
              fontWeight: shouldBeWhite ? "bold" : "normal",
              display: "block",
              lineHeight: "40px",
            }}
          >
            {cell.display}
          </span>
        </td>
      );
    });

    cells.push(
      <td
        key="total"
        style={{ textAlign: "right", fontWeight: "bold", padding: "0 10px" }}
      >
        {formatSeconds(rowData.total)}
      </td>,
    );

    cells.push(
      <td key="edit" style={{ textAlign: "center", padding: "0 5px" }}>
        <IconButton onClick={() => onEditClick(rowData.employee)}>
          <EditIcon color="primary" />
        </IconButton>
      </td>,
    );
    cells.push(
      <td key="delete" style={{ textAlign: "center", padding: "0 5px" }}>
        <IconButton onClick={() => onDeleteClick(rowData.employee)}>
          <DeleteIcon color="error" />
        </IconButton>
      </td>,
    );
    return cells;
  };

  return (
    <div
      className="table-stats"
      style={{ overflowX: "auto", marginTop: "20px" }}
    >
      <table
        className="data"
        style={{ borderCollapse: "collapse", width: "100%", fontSize: "14px" }}
      >
        <thead>
          <tr style={{ backgroundColor: "#f5f5f5" }}>
            <th
              className="text-left sticky-column"
              style={{ padding: "10px", borderBottom: "2px solid #ddd" }}
            >
              Dipendente
            </th>
            <RenderHeaderDays />
            <th
              className="text-right"
              style={{ padding: "10px", borderBottom: "2px solid #ddd" }}
            >
              Totale Ore
            </th>
            <th style={{ width: "50px" }}></th>
            <th style={{ width: "50px" }}></th>
          </tr>
        </thead>
        <tbody>
          {timeData.map((row, i) => (
            <tr
              key={row.employee.id || i}
              style={{ borderBottom: "1px solid #eee" }}
            >
              <td
                className="text-left sticky-column"
                style={{ padding: "10px", backgroundColor: "#fff", zIndex: 1 }}
              >
                <strong>{row.employee.name}</strong>
              </td>
              <RenderRowCells rowData={row} />
            </tr>
          ))}
        </tbody>
      </table>
    </div>
  );
};

export default AttendanceTable;
